import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, CircularProgress,
  Alert, Snackbar, useTheme, Grid, Tooltip, IconButton, Popover,
  Divider, Fade, Grow, Zoom, Slide, alpha, Card, Avatar
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  EmojiEmotions as EmojiIcon,
  ContactMail as ContactMailIcon,
  SubjectOutlined as SubjectIcon,
  Message as MessageIcon,
  MarkEmailRead as MarkEmailReadIcon
} from '@mui/icons-material';
import EmojiPicker from 'emoji-picker-react';
import { motion } from 'framer-motion';
import messageService from '../../../services/messageService';
import { useNotification } from '../../../contexts/NotificationContext';

const MemberMessageComposePage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const notification = useNotification();
  
  // Animation states
  const [pageLoaded, setPageLoaded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  useEffect(() => {
    // Start sequential animations when component mounts
    const timer1 = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    
    const timer2 = setTimeout(() => {
      setShowForm(true);
    }, 600);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const showLocalNotification = (message, severity = 'info') => {
    if (notification) {
      if (typeof notification.notify === 'function') {
        notification.notify(message, severity);
        return;
      } else if (typeof notification.showNotification === 'function') {
        notification.showNotification(message, severity);
        return;
      } else if (typeof notification.setNotification === 'function') {
        notification.setNotification({ message, severity, open: true });
        return;
      }
    }
    
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [errors, setErrors] = useState({});
  const [messageSent, setMessageSent] = useState(false);
  
  // Add states for emoji picker
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const openEmojiPicker = Boolean(emojiAnchorEl);

  // Function to handle opening emoji picker
  const handleEmojiPickerOpen = (event) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  // Function to handle closing emoji picker
  const handleEmojiPickerClose = () => {
    setEmojiAnchorEl(null);
  };

  // Function to handle emoji selection
  const onEmojiClick = (emojiObject) => {
    setMessage((prevMessage) => prevMessage + emojiObject.emoji);
    if (errors.message) {
      setErrors({...errors, message: undefined});
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSendMessage = async () => {
    if (!validateForm()) return;
    
    setSendingMessage(true);
    try {
      const messageData = {
        subject,
        text: message
      };
      
      const response = await messageService.sendMessageToAdmin(messageData);
      
      if (response.success) {
        setMessageSent(true);
        showLocalNotification('Message sent to admin successfully', 'success');
        
        // Short delay before redirecting for better UX
        setTimeout(() => {
          navigate('/member/messages');
        }, 2000);
      } else {
        showLocalNotification(response.error || 'Failed to send message', 'error');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      showLocalNotification('An error occurred while sending the message', 'error');
    } finally {
      setSendingMessage(false);
    }
  };
  
  const handleCancel = () => {
    navigate('/member/messages');
  };
  
  // Motion variants for animations
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  
  // Success animation when message is sent
  if (messageSent) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Fade in={true} timeout={800}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center',
            py: 8
          }}>
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, type: 'spring', stiffness: 200 }}
            >
              <Avatar 
                sx={{ 
                  width: 100, 
                  height: 100, 
                  bgcolor: theme.palette.success.main,
                  boxShadow: theme.shadows[5],
                  mb: 3
                }}
              >
                <MarkEmailReadIcon sx={{ fontSize: 60 }} />
              </Avatar>
            </motion.div>
            
            <Grow in={true} timeout={1000}>
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.primary.main})`,
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Message Sent Successfully!
              </Typography>
            </Grow>
            
            <Fade in={true} timeout={1500}>
              <Typography variant="body1" sx={{ mb: 4, maxWidth: 600, mx: 'auto', color: 'text.secondary' }}>
                Your message has been sent to the administrator. They will review your message and respond as soon as possible.
              </Typography>
            </Fade>
            
            <Fade in={true} timeout={2000}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/member/messages')}
                sx={{ 
                  mt: 2,
                  px: 4,
                  py: 1.2,
                  borderRadius: 8,
                  boxShadow: theme.shadows[3],
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: theme.shadows[5],
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Return to Messages
              </Button>
            </Fade>
          </Box>
        </Fade>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Fade in={pageLoaded} timeout={500}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleCancel}
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              transform: 'translateX(-5px)'
            }
          }}
        >
          Back to Messages
        </Button>
      </Fade>
      
      <Zoom in={pageLoaded} timeout={800}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 0, 
            mb: 3, 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[3],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Header */}
          <Box sx={{ 
            p: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
          }}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  sx={{ 
                    bgcolor: theme.palette.primary.main, 
                    width: 56, 
                    height: 56, 
                    mr: 2,
                    boxShadow: theme.shadows[3]
                  }}
                >
                  <SendIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Message to Admin
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Send a direct message to the system administrator
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Box>
          
          {/* Form */}
          <Fade in={showForm} timeout={800}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={3} component={motion.div} variants={formVariants} initial="hidden" animate="visible">
                <Grid item xs={12}>
                  <Card elevation={0} sx={{ 
                    p: 2, 
                    mb: 3, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      '& svg': { mr: 1, color: theme.palette.info.main } 
                    }}>
                      <ContactMailIcon />
                      Messages sent here are private and can only be seen by administrators. 
                      Please be specific about your question or concern to help us assist you better.
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    display: 'flex', 
                    alignItems: 'center' 
                  }}>
                    <SubjectIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    Subject
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Enter a clear and concise subject"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      if (e.target.value.trim()) {
                        setErrors({...errors, subject: undefined});
                      }
                    }}
                    error={!!errors.subject}
                    helperText={errors.subject}
                    InputProps={{
                      sx: {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.background.paper, 0.9),
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ 
                    mb: 1, 
                    fontWeight: 500, 
                    display: 'flex', 
                    alignItems: 'center' 
                  }}>
                    <MessageIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    Message
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={8}
                    placeholder="Type your message here..."
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      if (e.target.value.trim()) {
                        setErrors({...errors, message: undefined});
                      }
                    }}
                    error={!!errors.message}
                    helperText={errors.message}
                    InputProps={{
                      sx: {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.background.paper, 0.9),
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Tooltip title="Add Emoji">
                      <IconButton 
                        onClick={handleEmojiPickerOpen}
                        size="small" 
                        color="primary"
                        sx={{ 
                          mr: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                          }
                        }}
                      >
                        <EmojiIcon />
                      </IconButton>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {message.length} characters
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: 2, 
                    mt: 2,
                    '& button': {
                      borderRadius: 8,
                      px: 3,
                      py: 1,
                      transition: 'all 0.2s ease-in-out',
                    }
                  }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCancel}
                      sx={{
                        borderColor: alpha(theme.palette.text.secondary, 0.5),
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          borderColor: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={sendingMessage ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      onClick={handleSendMessage}
                      disabled={sendingMessage}
                      sx={{ 
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: theme.shadows[2],
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {sendingMessage ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        </Paper>
      </Zoom>
      
      {/* Emoji Picker Popover */}
      <Popover
        open={openEmojiPicker}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiPickerClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: theme.shadows[5],
            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
          }
        }}
      >
        <EmojiPicker 
          onEmojiClick={onEmojiClick} 
          lazyLoadEmojis={true}
        />
      </Popover>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[5]
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemberMessageComposePage;